import gql from 'graphql-tag'
import { connect } from 'react-redux'
import history from 'browserHistory'
import RoutesHelper from 'helpers/RoutesHelper'
import { compose } from 'recompose'

import {
  dataAdded,
  dataFetched,
  dataRemoved,
  makesQueries,
  makesMutations,
  extraDataFromResultAndDispatch,
} from 'data/graphql'

import {
  channelAttrsFragment,
  updateChannel as updateChannelMutation,
  leaveChannel as leaveChannelMutation,
  deleteChannel as deleteChannelMutation,
  publiclyShareChannel as publiclyShareChannelMutation,
  publiclyUnshareChannel as publiclyUnshareChannelMutation,
} from 'remote/channels'

import {
  viewIsLoading,
  viewHasLoaded,
  viewFailedToLoad,
  injectViewStateAsMeta,
} from 'data/ui/viewState'
import {
  createSuccessToasterMessage,
  createErrorToasterMessage,
  closeActiveModal,
  createConnectionWarningToasterMessage,
} from 'data/ui/actions'

import { getChannelViewName } from 'components/viewNames'

export { providesChannelsSubscriptions } from './subscriptions'

const channelsQuery = gql`
  query showChannel($channelId: ID!) {
    showChannel(channelId: $channelId) {
      ...channelAttrs
    }
  }
  ${channelAttrsFragment}
`

const syncsChannel = () =>
  compose(
    connect(
      null,
      {
        dataAdded,
        dataFetched,
        dataRemoved,
        viewIsLoading,
        viewFailedToLoad,
        createConnectionWarningToasterMessage,
        injectViewStateAsMeta,
      }
    ),
    makesQueries({
      query: channelsQuery,
      resyncPolicy: 'refetch',
      mapPropsToVariables: ({ channelId }) => ({ channelId }),
      getOnQueryFromProps: ({ viewIsLoading, channelId }) => () =>
        viewIsLoading(getChannelViewName(channelId), `loadChannel`),
      getOnResultFromProps: ({ injectViewStateAsMeta, channelId }) => ({
        result,
        meta,
      }) => {
        const channel = result.data.showChannel
        const dataFetchedAction = dataFetched(channel, meta)

        const viewHasLoadedAction = viewHasLoaded(
          getChannelViewName(channelId),
          `loadChannel`
        )
        injectViewStateAsMeta(dataFetchedAction, viewHasLoadedAction)
      },
      getOnErrorFromProps: ({
        viewFailedToLoad,
        channelId,
        createConnectionWarningToasterMessage,
      }) => ({ error, meta }) => {
        if (meta.isRefresh) {
          createConnectionWarningToasterMessage()
        } else {
          viewFailedToLoad(getChannelViewName(channelId), `loadChannel`, error)
        }
      },
    })
  )
export default syncsChannel

export const providesCreateChannelMutation = compose(
  connect(
    null,
    {
      dataFetched,
      closeActiveModal,
    }
  ),
  makesMutations({
    query: gql`
      mutation channelCreate($input: ChannelInput!) {
        channelCreate(input: $input) {
          channel {
            ...channelAttrs
          }
        }
      }
      ${channelAttrsFragment}
    `,
    mutationPropName: 'channelCreate',
    getOnResultFromProps: ({ dataFetched, onModalClose }) => ({ result }) => {
      dataFetched(result.data.channelCreate.channel)
      onModalClose()
      history.push(
        RoutesHelper.noteChannelIndex({
          channelId: result.data.channelCreate.channel.id,
        })
      )
    },
  })
)

export const providesChannelMutations = compose(
  connect(
    null,
    {
      dataFetched,
      dataRemoved,
      createSuccessToasterMessage,
      createErrorToasterMessage,
      closeActiveModal,
    }
  ),
  makesMutations({
    query: updateChannelMutation,
    mutationPropName: 'updateChannel',
    getOnResultFromProps: ({ dataFetched }) => ({ result }) => {
      dataFetched(result.data.updateChannel)
    },
  }),
  makesMutations({
    query: leaveChannelMutation,
    mutationPropName: 'leaveChannel',
    getOnResultFromProps: ({ dataRemoved, createSuccessToasterMessage }) => ({
      result,
    }) => {
      history.push(RoutesHelper.noteFilterIndex({ filterId: 'all' }))
      const { id, __typename } = result.data.leaveChannel
      dataRemoved({ id, __typename })
      createSuccessToasterMessage({
        title: 'You successfully left the channel',
      })
    },
    getOnErrorFromProps: ({ createErrorToasterMessage }) => ({ error }) => {
      createErrorToasterMessage({ title: error.message })
    },
  }),
  makesMutations({
    query: deleteChannelMutation,
    mutationPropName: 'deleteChannel',
    getOnResultFromProps: ({
      dataRemoved,
      createSuccessToasterMessage,
      closeActiveModal,
    }) => ({ result }) => {
      closeActiveModal()
      history.push(RoutesHelper.noteFilterIndex({ filterId: 'all' }))
      const { id } = result.data.deleteChannel
      dataRemoved({ id, __typename: 'Channel' })
      createSuccessToasterMessage({
        title: 'You successfully deleted the channel',
      })
    },
    getOnErrorFromProps: ({ createErrorToasterMessage }) => ({ error }) => {
      createErrorToasterMessage({ title: error.message })
    },
  }),
  makesMutations({
    query: publiclyShareChannelMutation,
    mutationPropName: 'publiclyShareChannel',
    getOnResultFromProps: extraDataFromResultAndDispatch(
      'publiclyShareChannel'
    ),
  }),
  makesMutations({
    query: publiclyUnshareChannelMutation,
    mutationPropName: 'publiclyUnshareChannel',
    getOnResultFromProps: extraDataFromResultAndDispatch(
      'publiclyUnshareChannel'
    ),
  })
)
